import { Component, ViewChild } from '@angular/core';
import { ApiResponseModel } from 'app/_models/apiResponseModel';
import { GetByPageResponse } from './_models/getByPageResponse';
import { ActivatedRoute } from '@angular/router';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { NotificationService } from 'app/_services/notification.service';
import { Globals } from 'app/_models/globals';
import { SupportIntegrationManagementService } from './integration-management.service';
import { SupportManageIntegrationComponent } from './_components/integration-manage/integration-manage.component';
import { HoldingIntegration } from './_models/holdingIntegration';
import { SaveHoldingIntegration } from './_models/saveHoldingIntegration';
import { SupportGridview, SupportGridviewTableBodyCell } from 'app/support/_models/supportGridview';
import { Subscription } from 'rxjs';

declare const isEmpty: any;
@Component({
    selector: 'app-manage-integrations', templateUrl: './integration-management.component.html', styleUrls: ['./integration-management.component.scss'],
    standalone: false
})
export class SupportIntegrationManagement {
    @ViewChild('templateIntegrationManageComponent') public holdingIntegrationManageComponent: SupportManageIntegrationComponent;

    private _sortSubscription: Subscription;

    actualPage: number = 0;
    config: SupportGridview = new SupportGridview();
    integrations: HoldingIntegration[] = [];
    isList: boolean = true;
    isLoading: boolean = false;
    searchString: string = '';
    selectedIntegrationToEdit: HoldingIntegration = null;
    pageSize: number = 30;
    totalIntegrations: number;

    constructor(private supportIntegrationService: SupportIntegrationManagementService, private route: ActivatedRoute, private notificationService: NotificationService, private ngxLoader: NgxUiLoaderService, private globals: Globals) {
    }

    ngOnInit() {
        let param_id = this.route.snapshot.params.id;

        this._sortSubscription = this.supportIntegrationService.sortDataValue$.subscribe(value => this.config.sort = value);
        this.config.set({
            actions: {
                edit: { enable: true, tooltipText: 'support.manage-integrations.tooltip.edit', type: 'icon' },
            },
            emptyText: { isHtml: false, value: 'support.manage-integrations.list.empty-list' },
            list: [],
            sort: this.supportIntegrationService.getSortDataValue(),
            table: {
                body: [
                    new SupportGridviewTableBodyCell('col col-1', 'holdingId', 'string'),
                    new SupportGridviewTableBodyCell('col-2 truncate-gridview', 'holdingName', 'string'),
                    new SupportGridviewTableBodyCell('col-2 truncate-gridview', 'lastUploadDate', 'date'),
                    new SupportGridviewTableBodyCell('col-2 truncate-gridview', 'integrationTypeDescription', 'string')
                ],
                header: [
                    { className: 'col col-1', sortName: 'holdingId', value: 'support.manage-integrations.list.table-header.id' },
                    { className: 'col col-2', sortName: 'holdingName', value: 'support.manage-integrations.list.table-header.holding-name' },
                    { className: 'col col-2', sortName: 'lastUploadDate', value: 'support.manage-integrations.list.table-header.last-update' },
                    { className: 'col col-2', sortName: 'integrationType', value: 'support.manage-integrations.list.table-header.integration-type' },
                    { className: 'col col-0-5 text-center', value: 'support.manage-integrations.list.table-header.actions' }
                ],
            }
        });

        if(!isEmpty(param_id))
            return this.getById(param_id);

        this.getByPage();
    }

    ngOnDestroy(): void {
        if (!this._sortSubscription?.closed)
            this._sortSubscription.unsubscribe();
    }

    changePage(event: { pageIndex: number; pageSize: number }) {
        this.actualPage = event.pageIndex;
        this.getByPage(this.actualPage);
    }

    getById(holdingId: string) {
        this.ngxLoader.startLoader('loader-principal');

        this.supportIntegrationService.getById(holdingId).subscribe({
            next: (response: ApiResponseModel<HoldingIntegration>) => {
                let integration = response.result;

                if (integration) {
                    this.openIntegrationEdition(integration);
                    this.ngxLoader.stopLoader('loader-principal');
                } else {
                    this.goBackToList();
                }
            },
            error: error => {
                console.log(error);
                this.ngxLoader.stopLoader('loader-principal');
                this.notificationService.showErrorToastr(this.globals.translate('support.manage-holdings.messages.error.get-one'));
                this.goBackToList();
            }
        });
    }

    getByPage(pageNumber: number = 0) {
        this.ngxLoader.startLoader('loader-principal');

        this.supportIntegrationService.getByPage(pageNumber, this.pageSize, this.searchString.toLowerCase()).subscribe({
            next: (response: ApiResponseModel<GetByPageResponse>) => {
                if (response && response.success) {
                    this.actualPage = pageNumber;
                    this.totalIntegrations = response.result.totalIntegrations;
                    this.integrations = response.result.integrations;
                    this.setSupportGridViewData();
                } else {
                    this.actualPage = 0;
                    this.totalIntegrations = 0;
                    this.integrations = [];
                    this.notificationService.showErrorToastr(this.globals.translate('support.manage-integrations.messages.error.list'));
                }
                this.ngxLoader.stopLoader('loader-principal');
            },
            error: error => {
                console.log(error);
                this.ngxLoader.stopLoader('loader-principal');
                this.notificationService.showErrorToastr(this.globals.translate('support.manage-integrations.messages.error.list'));
            }
        });
    }

    goBackToList() {
        if (this.integrations.length == 0)
            this.getByPage();

        this.isList = true;
        this.selectedIntegrationToEdit = null;
        history.pushState({}, '', '/suporte/manutencao/integracoes');
    }

    handleIntegrationSave(integration: SaveHoldingIntegration) {
        return this.upsertIntegration(integration);
    }

    handleIntegrationUpdate(integration: HoldingIntegration) {
        this.selectedIntegrationToEdit = integration;
    }

    openIntegrationEdition(integration: HoldingIntegration) {
        this.selectedIntegrationToEdit = integration;
        history.pushState({}, '', '/suporte/manutencao/integracoes/editar/' + integration.holdingId);
        this.isList = false;
    }

    openNewIntegration() {
        this.selectedIntegrationToEdit = null;
        history.pushState({}, '', '/suporte/manutencao/integracoes/novo' );
        this.isList = false;
    }

    upsertIntegration(integration: SaveHoldingIntegration) {
        this.ngxLoader.startLoader('loader-principal');

        let isEdit = !isEmpty(this.selectedIntegrationToEdit?.integrationId);
        let integrationCall = isEdit ? this.supportIntegrationService.editHoldingIntegration(integration) : this.supportIntegrationService.createHoldingIntegration(integration);

        integrationCall.subscribe({
            next: (response: ApiResponseModel<boolean>) => {
                if (response.success) {
                    this.getByPage();
                    this.goBackToList();

                    let messageLabel = isEdit ? 'edit' : 'create';
                    this.notificationService.showSuccessToastr(this.globals.translate(`support.manage-integrations.messages.success.${messageLabel}`));
                } else {
                    this.isLoading = false;
                    this.notificationService.showErrorToastr(this.globals.translate('support.manage-integrations.messages.error.edit'));
                }

                this.ngxLoader.stopLoader('loader-principal');
            },
            error: error => {
                console.log(error);
                this.ngxLoader.stopLoader('loader-principal');
                this.isLoading = false;
                this.notificationService.showErrorToastr(this.globals.translate('support.manage-integrations.messages.error.edit'));
            }
        });
    }

    setSupportGridViewData() {
        this.config.list = this.integrations;
    }

    get searchTerm(): string {
        return this.searchString;
    }

    set searchTerm(value: string) {
        this.searchString = value;
        this.getByPage();
    }
}
